import { useState, useRef, useEffect } from 'react'
import { Send, Sparkles, Check, ChevronDown } from 'lucide-react'
import { useSendCaptionChatMutation } from '@/redux/collaborationsApi'

interface Message {
  role: 'assistant' | 'user' | 'system'
  content: string
  suggestedValue?: string
  applied?: boolean
}

interface AiCaptionAssistantChatProps {
  campaignId: string
  brandName: string
  platform: string
  onApplyCaption: (value: string) => void
  onClose: () => void
}

function getOpener(brandName: string, platform: string): string {
  return `Let's write your post caption for ${brandName} on ${platform}. Tell me the vibe you're going for — funny, authentic, low-key, hype — and I'll help you nail it.`
}

const AiCaptionAssistantChat = ({
  campaignId,
  brandName,
  platform,
  onApplyCaption,
  onClose,
}: AiCaptionAssistantChatProps) => {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: getOpener(brandName, platform) },
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastAiMessageRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [sendChat] = useSendCaptionChatMutation()

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return
    const lastMsg = messages[messages.length - 1]
    if (isLoading || !lastMsg || lastMsg.role === 'user' || lastMsg.role === 'system') {
      container.scrollTop = container.scrollHeight
    } else if (lastMsg.role === 'assistant' && lastAiMessageRef.current) {
      const containerRect = container.getBoundingClientRect()
      const msgRect = lastAiMessageRef.current.getBoundingClientRect()
      container.scrollTop += msgRect.top - containerRect.top - 20
    }
  }, [messages, isLoading])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 280)
  }

  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return

    const nextMessages: Message[] = [...messages, { role: 'user', content: trimmed }]
    setMessages(nextMessages)
    setInput('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    setIsLoading(true)

    try {
      const apiMessages = nextMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }))

      const result = await sendChat({ campaignId, messages: apiMessages }).unwrap()

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.reply,
          suggestedValue: result.suggestedCaption ?? undefined,
        },
      ])
    } catch (err: any) {
      const errorText =
        err?.status === 429
          ? 'AI usage limit reached for this month.'
          : 'Something went wrong. Please try again.'
      setMessages((prev) => [...prev, { role: 'assistant', content: errorText }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i, arr) => {
      const parts = line.split(/("([^"]{20,})")/)
      return (
        <span key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('"') && part.endsWith('"') && part.length > 22) {
              return (
                <span
                  key={j}
                  className="block mt-2 mb-1 text-white/90 bg-white/8 rounded-xl px-3 py-2.5 text-sm italic border border-white/10"
                >
                  {part}
                </span>
              )
            }
            return <span key={j}>{part}</span>
          })}
          {i < arr.length - 1 && <br />}
        </span>
      )
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      <div
        className={`relative flex flex-col bg-[#0f1623] rounded-t-3xl transition-transform duration-300 ease-out ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ height: '88dvh' }}
      >
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C0FD72]" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">
                AI Caption Assistant
              </p>
              <p className="text-gray-500 text-xs">
                {brandName} · {platform}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-4 py-5 space-y-5 overscroll-contain"
        >
          {(() => {
            const lastAiIdx = messages.reduce(
              (last, m, idx) => (m.role === 'assistant' ? idx : last),
              -1,
            )
            return messages.map((msg, i) =>
              msg.role === 'system' ? (
                <div key={i} className="flex justify-center py-1">
                  <div className="flex items-center gap-1.5 bg-[#C0FD72]/10 border border-[#C0FD72]/20 rounded-full px-3 py-1.5">
                    <Check className="w-3 h-3 text-[#C0FD72]" />
                    <span className="text-[#C0FD72] text-xs font-medium">{msg.content}</span>
                  </div>
                </div>
              ) : (
                <div
                  key={i}
                  ref={msg.role === 'assistant' && i === lastAiIdx ? lastAiMessageRef : null}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/25 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#C0FD72]" />
                    </div>
                  )}
                  <div className={`max-w-[82%] ${msg.role === 'assistant' ? 'space-y-3' : ''}`}>
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#C0FD72] text-black rounded-tr-sm font-medium'
                          : 'bg-white/8 text-gray-200 rounded-tl-sm border border-white/8'
                      }`}
                    >
                      {msg.role === 'assistant' ? renderContent(msg.content) : msg.content}
                    </div>
                    {msg.suggestedValue && (
                      <div className="bg-white/6 border border-white/12 rounded-2xl px-4 py-3 text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">
                        {msg.suggestedValue}
                      </div>
                    )}
                    {msg.suggestedValue && (
                      <button
                        onClick={() => {
                          onApplyCaption(msg.suggestedValue!)
                          setMessages((prev) =>
                            prev
                              .map((m, idx) => (idx === i ? { ...m, applied: true } : m))
                              .concat({
                                role: 'system',
                                content:
                                  'Applied to your caption. Keep refining or close to review it.',
                              }),
                          )
                        }}
                        disabled={msg.applied}
                        className={`w-full text-sm font-semibold py-3 px-4 rounded-2xl transition-colors text-left flex items-center gap-2 ${
                          msg.applied
                            ? 'bg-white/10 text-gray-400 cursor-default'
                            : 'bg-[#C0FD72] text-black hover:bg-[#b0ef62]'
                        }`}
                      >
                        {msg.applied ? (
                          <>
                            <Check className="w-4 h-4 shrink-0" />
                            Applied to your caption
                          </>
                        ) : (
                          'Use this caption \u2192'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ),
            )
          })()}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-7 h-7 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/25 flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#C0FD72]" />
              </div>
              <div className="bg-white/8 border border-white/8 rounded-2xl rounded-tl-sm px-5 py-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:160ms]" />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:320ms]" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 px-4 pb-8 pt-3 border-t border-white/8 bg-[#0f1623]">
          <div className="flex items-end gap-3 bg-white/8 border border-white/12 rounded-2xl px-4 py-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                autoGrow(e.target)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Describe your vibe or ask for a tweak..."
              className="flex-1 bg-transparent text-white placeholder:text-gray-600 text-base resize-none outline-hidden leading-relaxed min-h-[24px]"
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="w-10 h-10 bg-[#C0FD72] hover:bg-[#b0ef62] disabled:opacity-30 text-black rounded-full flex items-center justify-center shrink-0 transition-all disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AiCaptionAssistantChat
